<template>
  <!-- The robots' control body (card t_390bc438): the vacuum's and the mower's
       sheet, one body for both. The host (`DeviceControlModal`, the dashboard
       card, the room page's full-control card) owns the head and the panel; this
       owns the hero and the rows, so the two machines cannot drift apart.

       Everything the machine REPORTS is a reading and is drawn as one — the
       operational state, the battery, the progress rail, the run mode, the
       schedule stamps and the error strip. The only writes on this sheet are the
       verbs (`command`) and the operator's own pick (`clean_mode`), and each of
       those is gated on the hub's `modeSettable` flag, so a device whose row
       says read-only renders read-only. -->
  <div class="robot-sheet" :style="{ '--sheet-accent': accent }">
    <!-- HERO — the machine itself, drawn at sheet size (the same drawing its
         tile shows; the disc works the floor while the state says Running), then
         the state chip and the job line under it. -->
    <div class="robot-hero">
      <div class="robot-hero-figure" :class="{ 'robot-hero-figure--active': running }">
        <SwitchAnimation :kind="kind" :active="running" />
      </div>
      <div class="robot-state-chip" :class="stateChipClass">{{ stateWord }}</div>
      <div v-if="heroCaption" class="robot-hero-caption">{{ heroCaption }}</div>
    </div>

    <!-- PROGRESS — the job the machine is on, as a rail: the reading, not a
         control (Matter has no progress attribute; this is the hub's own). -->
    <div v-if="progress !== null" class="robot-progress">
      <div class="robot-row-head">
        <span class="robot-row-key">{{ tt('Progress', 'Napredek') }}</span>
        <span class="robot-row-value">{{ progress }}%</span>
      </div>
      <div class="robot-rail" role="img" :aria-label="`${progress}%`">
        <span class="robot-rail__fill" :class="{ 'robot-rail__fill--working': running }" :style="{ width: `${progress}%` }" />
      </div>
    </div>

    <!-- READINGS — battery, the run mode the machine reports, and (the mower
         only) the two schedule stamps the house's day cycle writes. -->
    <div class="robot-tiles">
      <ControlValueTile
        icon="bolt"
        :label="tt('Battery', 'Baterija')"
        :value="batteryText"
        :unit="battery === null ? '' : '%'"
        :tone="charging ? 'accent' : 'muted'" />
      <ControlValueTile
        v-if="runModeWord"
        icon="activity"
        :label="tt('Run mode', 'Način delovanja')"
        :value="runModeWord" />
      <ControlValueTile
        v-if="lastRun"
        icon="clock"
        :label="tt('Last run', 'Zadnja košnja')"
        :value="lastRun" />
      <!-- `next_run` is settable on the hub's mower class, but the schedule it
           carries is the house's own (the day cycle stamps it). The sheet shows
           it as the reading it is; a schedule editor is not this card's. -->
      <ControlValueTile
        v-if="nextRun"
        icon="timer"
        :label="tt('Next run', 'Naslednja košnja')"
        :value="nextRun" />
    </div>

    <!-- VERBS — the machine's own command set. The four it always offers
         (Start · Pause · Stop · Dock) stay on the sheet in the hub's order and
         are dimmed where they do not apply; Resume joins them while the robot
         is paused, which is the one state it belongs to. Which phase the
         machine is IN is the state chip's job above, so no chip is marked
         "current" here — a lit Start on a running robot read as a live action. -->
    <div class="robot-actions">
      <ControlChip
        v-for="verb in verbRow"
        :key="verb"
        :class="{ 'robot-verb--off': !verbApplies(verb) }"
        :disabled="controlling || !verbApplies(verb)"
        @click="sendVerb(verb)">
        {{ verbWord(verb) }}
      </ControlChip>
    </div>

    <!-- THE OPERATOR'S PICK — the vacuum's suction, the mower's cut height, on
         the machine's own enum row. -->
    <div v-if="hasCleanMode" class="robot-modes">
      <div class="robot-row-head">
        <span class="robot-row-key">{{ cleanModeTitle }}</span>
      </div>
      <div class="robot-chips">
        <ControlChip
          v-for="option in cleanModeOptions"
          :key="option.value"
          :active="option.value === cleanMode"
          :disabled="controlling || !cleanModeWritable"
          small
          @click="writeCleanMode(option.value)">
          {{ cleanModeWord(option.value) }}
        </ControlChip>
      </div>
    </div>

    <!-- ERROR / PROTECTION STRIP — the machine's own `error` string, a state
         that says Error without one, or the legacy free-text status. Nothing is
         drawn when the machine reports nothing. -->
    <div v-if="notice" class="robot-notice" :class="`robot-notice--${notice.tone}`">
      <ControlIcon :name="notice.tone === 'warn' ? 'shield-off' : 'shield'" size="1.0625rem" />
      <span>{{ notice.text }}</span>
    </div>

    <p v-if="lastUpdated" class="robot-meta">
      <ControlIcon name="clock" size="0.8125rem" />
      {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}
    </p>

    <SetpointTimeline :device-values="device.deviceValues ?? []" :device-id="device.id" :accent-color="accent" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { formatTime } from '../../../utils/formatDate';
import SwitchAnimation from '../switch/SwitchAnimation.vue';
import ControlChip from '../../device-sheet/ControlChip.vue';
import ControlValueTile from '../../device-sheet/ControlValueTile.vue';
import ControlIcon from '../../device-icons/ControlIcon.vue';
import SetpointTimeline from '../SetpointTimeline.vue';
import { useDeviceValues } from '../../../composables/useDeviceValues';
import { useDeviceControl } from '../../../composables/useDeviceControl';
import { useDeviceValueOptionsStore } from '../../../stores/deviceValueOptions';
import { tt } from '../../../utils/consoleText';
import {
  ROBOT_BATTERY_KEY,
  ROBOT_CLEAN_MODE_KEY,
  ROBOT_COMMAND_KEY,
  ROBOT_COMMAND_LABELS,
  ROBOT_COMMANDS,
  ROBOT_CLEAN_MODE_LABELS,
  ROBOT_ERROR_KEY,
  ROBOT_LAST_RUN_KEY,
  ROBOT_NEXT_RUN_KEY,
  ROBOT_PROGRESS_KEY,
  ROBOT_RUN_MODE_LABELS,
  ROBOT_STATUS_KEY,
  ROBOT_WORDS,
  robotCleanMode,
  robotDocked,
  robotEnumOptions,
  robotHasResume,
  robotPercent,
  robotRunMode,
  robotRunning,
  robotState,
  robotStateWord,
  robotText,
  robotVerbApplies,
  robotWritable,
  type RobotCommand,
  type RobotKind,
} from '../../../utils/robotState';
import type { Device } from '@/types';

const props = defineProps<{
  device: Device;
  /** Which machine this is — the vocabulary (suction vs cut height) and the drawing. */
  kind: RobotKind;
}>();

/** The family's own accent — vacuum teal, mower lime (the `--device-color-*` pair). */
const ACCENTS: Record<RobotKind, string> = {
  vacuum: 'var(--device-color-vacuum)',
  mower: 'var(--device-color-mower)',
};

const accent = computed(() => ACCENTS[props.kind]);

// ── State ────────────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty } = useDeviceControl();
const deviceValueOptionsStore = useDeviceValueOptionsStore();
const fetchedCleanModes = ref<{ value: string; label: string }[] | null>(null);

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

/** The device's value rows — the sheet's row gate and the read-only fold. */
const rows = computed(() => props.device.deviceValues ?? []);

// ── Readings ─────────────────────────────────────────────────────────────
const state = computed(() => robotState(rows.value, currentValues.value));
const running = computed(() => robotRunning(state.value));
const charging = computed(() => robotDocked(state.value));
const battery = computed(() => robotPercent(rows.value, currentValues.value, ROBOT_BATTERY_KEY));
const progress = computed(() => robotPercent(rows.value, currentValues.value, ROBOT_PROGRESS_KEY));
const runMode = computed(() => robotRunMode(rows.value, currentValues.value));
const cleanMode = computed(() => robotCleanMode(rows.value, currentValues.value));
const lastRun = computed(() => robotText(rows.value, currentValues.value, ROBOT_LAST_RUN_KEY));
const nextRun = computed(() => robotText(rows.value, currentValues.value, ROBOT_NEXT_RUN_KEY));

/**
 * The state chip's word. The hub's own RVC names, in both languages — and `--`
 * for a device that has not reported one, never an invented word.
 */
const stateWord = computed(() => {
  const pair = robotStateWord(state.value);
  return pair ? tt(pair.en, pair.sl) : '--';
});

const stateChipClass = computed(() => {
  if (state.value === 'Error') return 'robot-state-chip--error';
  if (running.value) return 'robot-state-chip--running';
  if (charging.value) return 'robot-state-chip--home';
  return 'robot-state-chip--idle';
});

/**
 * The line under the state chip: what the machine is doing with its job, or
 * where it is — the same "reading under the word" the garage door's caption is.
 */
const heroCaption = computed(() => {
  const current = state.value;
  if (current === null) return '';
  if (current === 'Running' || current === 'Paused') {
    return progress.value === null ? '' : tt(`${progress.value}% of the job`, `${progress.value}% opravila`);
  }
  if (robotDocked(current)) {
    const words = ROBOT_WORDS[props.kind].docked;
    return tt(words.en, words.sl);
  }
  if (current === 'SeekingCharger') return tt('Heading for the dock', 'Na poti na postajo');
  if (current === 'Error') return tt('Stopped on an error', 'Ustavljen zaradi napake');
  return tt('Ready to start', 'Pripravljen na začetek');
});

const batteryText = computed(() => (battery.value === null ? '--' : String(battery.value)));

/** The run-mode word: this build's pair, else the hub's own word, else nothing. */
const runModeWord = computed(() => {
  if (runMode.value === '') return '';
  const pair = ROBOT_RUN_MODE_LABELS[runMode.value];
  return pair ? tt(pair.en, pair.sl) : runMode.value;
});

const cleanModeTitle = computed(() => {
  const words = ROBOT_WORDS[props.kind].cleanMode;
  return tt(words.en, words.sl);
});

/** The device's own choices for the picker, else this build's list for the kind. */
const cleanModeOptions = computed<{ value: string; label: string }[]>(() => {
  if (fetchedCleanModes.value?.length) return fetchedCleanModes.value;
  const declared = robotEnumOptions(rows.value, ROBOT_CLEAN_MODE_KEY);
  if (declared.length) return declared;
  return ROBOT_WORDS[props.kind].cleanModes.map((value) => ({ value, label: value }));
});

/** A clean-mode word: this build's pair, else the option's own label, else the raw value. */
function cleanModeWord(value: string): string {
  const pair = ROBOT_CLEAN_MODE_LABELS[value];
  if (pair) return tt(pair.en, pair.sl);
  return cleanModeOptions.value.find((option) => option.value === value)?.label ?? value;
}

/** The notice strip: the machine's error, an errored state, or its legacy status. */
const notice = computed<{ text: string; tone: 'warn' | 'info' } | null>(() => {
  const error = robotText(rows.value, currentValues.value, ROBOT_ERROR_KEY);
  if (error) return { text: error, tone: 'warn' };
  if (state.value === 'Error') {
    return { text: tt('The robot reported an error.', 'Robot je javil napako.'), tone: 'warn' };
  }
  const status = robotText(rows.value, currentValues.value, ROBOT_STATUS_KEY);
  return status ? { text: status, tone: 'info' } : null;
});

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

// ── Writes ───────────────────────────────────────────────────────────────
const commandWritable = computed(() => robotWritable(rows.value, ROBOT_COMMAND_KEY));
const hasCleanMode = computed(() => rows.value.some((row) => String(row.valueKey ?? '') === ROBOT_CLEAN_MODE_KEY));
const cleanModeWritable = computed(() => robotWritable(rows.value, ROBOT_CLEAN_MODE_KEY));

/**
 * The verbs on the sheet, in the hub's own order: the four the machine always
 * offers (`Start · Pause · Stop · Dock`), with `Resume` joining them only where
 * it applies — which is the paused state. The inapplicable ones stay visible
 * and dim (the row is a stable map of the machine's control set; a control that
 * disappears is a control the operator cannot find).
 */
const verbRow = computed<RobotCommand[]>(() =>
  ROBOT_COMMANDS.filter((verb) => verb !== 'Resume' || robotHasResume(state.value)),
);

/** The verb's own word, in both languages. */
function verbWord(verb: RobotCommand): string {
  const pair = ROBOT_COMMAND_LABELS[verb];
  return tt(pair.en, pair.sl);
}

/** Whether a verb applies in the state the machine reports (the chip's own enabled reading). */
function verbApplies(verb: RobotCommand): boolean {
  return commandWritable.value && robotVerbApplies(state.value, verb);
}

async function sendVerb(verb: RobotCommand): Promise<void> {
  if (controlling.value || !verbApplies(verb)) return;
  await setProperty(props.device, ROBOT_COMMAND_KEY, verb);
}

async function writeCleanMode(value: string): Promise<void> {
  if (controlling.value || !cleanModeWritable.value || value === cleanMode.value) return;
  await setProperty(props.device, ROBOT_CLEAN_MODE_KEY, value);
}

/**
 * The class' enum choices come from the options endpoint (they are not stored
 * on the device row); the kind's own list is the fallback, so the picker is
 * never empty.
 */
onMounted(async () => {
  const row = props.device.deviceValues?.find((value) => value.valueKey === ROBOT_CLEAN_MODE_KEY);
  if (!row?.id) return;
  try {
    const options = await deviceValueOptionsStore.fetchOptions(row.id);
    if (options.enum_options.length) {
      fetchedCleanModes.value = options.enum_options.map((option) => ({
        value: String(option.value),
        label: String(option.label ?? ''),
      }));
    }
  } catch {
    // Fall back to the kind's own list.
  }
});
</script>

<style scoped>
/* ── The robots' sheet ───────────────────────────────────────────────────
   The hero drawing, the state chip, the progress rail, the reading tiles, the
   verbs and the operator's pick — every accent reads `--sheet-accent`, which
   the body root hands down (the family's own colour). */

.robot-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(1rem * var(--ui-space-factor));
  min-width: 0;
}

.robot-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding-top: 0.25rem;
}

.robot-hero-figure {
  width: 7.5rem;
  height: 7.5rem;
  color: rgb(148, 163, 184);
  transition: color 0.3s ease;
}

.dark .robot-hero-figure {
  color: rgb(100, 116, 139);
}

.robot-hero-figure--active {
  color: var(--sheet-accent);
}

/* The state chip: the RVC state, in the family's colour while the machine is
   away, its amber while it is home, alarm red on an error. */
.robot-state-chip {
  display: inline-flex;
  align-items: center;
  padding: calc(0.1875rem * var(--ui-space-factor)) calc(0.625rem * var(--ui-space-factor));
  border: 1px solid var(--glass-edge);
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: rgb(var(--gray-600));
  background: rgba(148, 163, 184, 0.1);
}

.dark .robot-state-chip {
  color: rgb(var(--gray-300));
  background: rgba(255, 255, 255, 0.04);
}

.robot-state-chip--running {
  border-color: color-mix(in srgb, var(--sheet-accent) 55%, transparent);
  background: color-mix(in srgb, var(--sheet-accent) 14%, transparent);
  color: var(--sheet-accent);
}

.robot-state-chip--home {
  border-color: color-mix(in srgb, var(--device-color-strike-lock) 50%, transparent);
  background: color-mix(in srgb, var(--device-color-strike-lock) 13%, transparent);
  color: var(--device-color-strike-lock);
}

.robot-state-chip--error {
  border-color: color-mix(in srgb, var(--device-color-alarm) 55%, transparent);
  background: color-mix(in srgb, var(--device-color-alarm) 14%, transparent);
  color: var(--device-color-alarm);
}

.robot-hero-caption {
  font-size: 0.6875rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
  text-align: center;
}

.dark .robot-hero-caption {
  color: rgb(var(--gray-400));
}

/* ── The readings rows ─────────────────────────────────────────────────── */
.robot-progress {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
}

.robot-row-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.robot-row-key {
  font-size: 0.8125rem;
  color: rgb(var(--gray-600));
}

.dark .robot-row-key {
  color: rgb(var(--gray-300));
}

.robot-row-value {
  font-size: 0.9375rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--gray-900));
}

.dark .robot-row-value {
  color: #fff;
}

/* The rail is a READING, not a control — the same bar the garage door's
   opening draws, striped while the machine is working it. */
.robot-rail {
  position: relative;
  height: 0.45em;
  border-radius: 9999px;
  background: color-mix(in srgb, currentColor 12%, transparent);
  overflow: hidden;
}

.robot-rail__fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--sheet-accent);
  transition: width 0.25s ease;
}

.robot-rail__fill--working {
  background: repeating-linear-gradient(
    45deg,
    var(--sheet-accent) 0 8px,
    color-mix(in srgb, var(--sheet-accent) 55%, transparent) 8px 16px
  );
}

.robot-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(8.5rem, 1fr));
  gap: calc(0.5rem * var(--ui-space-factor));
}

/* ── The verbs and the picker ──────────────────────────────────────────── */
.robot-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: calc(0.5rem * var(--ui-space-factor));
}

/* A verb that does not apply in the state the machine reports. `ControlChip`
   draws no disabled style of its own (its `disabled` is a fall-through
   attribute), so the sheet dims the ones its own table rules out. */
.robot-actions :deep(.robot-verb--off) {
  opacity: 0.45;
}

.robot-modes {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
}

.robot-chips {
  display: flex;
  flex-wrap: wrap;
  gap: calc(0.375rem * var(--ui-space-factor));
}

/* ── The error / protection strip ──────────────────────────────────────── */
.robot-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0;
  padding: calc(0.5rem * var(--ui-space-factor)) calc(0.75rem * var(--ui-space-factor));
  border: 1px solid var(--glass-edge);
  border-radius: calc(0.625rem * var(--ui-radius-factor));
  font-size: 0.8125rem;
  line-height: 1.3;
  background: rgba(148, 163, 184, 0.1);
  color: rgb(var(--gray-600));
}

.dark .robot-notice {
  background: rgba(255, 255, 255, 0.04);
  color: rgb(var(--gray-300));
}

.robot-notice--warn {
  border-color: color-mix(in srgb, var(--device-color-alarm) 45%, transparent);
  background: color-mix(in srgb, var(--device-color-alarm) 12%, transparent);
  color: var(--device-color-alarm);
}

.robot-meta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35em;
  margin: 0;
  font-size: 0.6875rem;
  color: rgb(var(--gray-500));
}

.dark .robot-meta {
  color: rgb(var(--gray-400));
}
</style>
